// src/components/Chat/MessageFormatter.ts
export class MessageFormatter {
  static formatMessage(content: string): string {
    if (!content) return "";

    // 1) Normalize line endings
    let t = content.replace(/\r\n/g, "\n");

    // 2) Capture trailing "Sources:" block into a map, then remove it
    const sourcesMap: Record<string, string> = {};
    (() => {
      const m = t.match(/(?:^|\n)Sources?\s*:?\s*([\s\S]*)$/i);
      if (!m) return;
      const rest = m[1] || "";
      const re = /\[(\d+)\]\s*(https?:\/\/\S+)/gi;
      let mm: RegExpExecArray | null;
      while ((mm = re.exec(rest))) {
        sourcesMap[mm[1]] = mm[2];
      }
      // Remove entire trailing Sources section
      t = t.replace(/(?:^|\n)Sources?\s*:?\s*[\s\S]*$/i, "");
      t = t.trimEnd();
    })();

    // Helpers to protect & restore code regions so we don't mangle them
    type SafeBlock = { ph: string; val: string };
    const fences: SafeBlock[] = [];
    const inlines: SafeBlock[] = [];

    const save = (arr: SafeBlock[], val: string, tag: "FENCE" | "INLINE") => {
      const ph = `@@_${tag}_${arr.length}_@@`;
      arr.push({ ph, val });
      return ph;
    };
    const restoreAll = (s: string, arr: SafeBlock[]) =>
      arr.reduce((acc, item) => acc.replace(item.ph, item.val), s);

    // 3) Protect fenced code blocks ```...```
    t = t.replace(/```([\s\S]*?)```/g, (_m, code) => {
      const block = "```" + code + "```";
      return save(fences, block, "FENCE");
    });

    // 4) Protect inline code `...`
    t = t.replace(/`([^`]+)`/g, (_m, code) => {
      const block = "`" + code + "`";
      return save(inlines, block, "INLINE");
    });

    // 5) Inline formatting in Markdown-space (keep it minimal & safe)
    // 5a) Autolink bare URLs (wrap in <...>) without touching existing markdown links
    //     We’ll avoid matching if already inside [](...)
    t = t.replace(
      /(^|[\s(])((?:https?:\/\/|www\.)[^\s<)]+)(?=[\s)]|$)/gi,
      (_m, prefix: string, url: string) => {
        const m2 = url.match(/^(.*?)([.,;:!?)\]]*)$/);
        const clean = m2 ? m2[1] : url;
        const trail = m2 ? m2[2] : "";
        const href = /^https?:\/\//i.test(clean) ? clean : `https://${clean}`;
        return `${prefix}<${href}>${trail}`;
      }
    );

    // 5b) Autolink bare emails
    t = t.replace(
      /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
      (m) => `<mailto:${m}>`
    );

    // 5c) Upgrade bare numeric citations [1] using sourcesMap if available
    if (Object.keys(sourcesMap).length) {
      // Don’t touch ones that are already links: [1](...)
      t = t.replace(/\[(\d+)\](?!\()/g, (m, num: string) => {
        const url = sourcesMap[num];
        return url ? `[${m}](${url})` : m;
      });
    }

    // 6) (Optional) Light whitespace cleanup around lists; keep user’s Markdown intact
    //    We won’t transform to HTML lists anymore — Markdown renderers will do that.
    //    Just ensure no accidental extra spaces after bullets.
    t = t
      .split("\n")
      .map((line) =>
        line.replace(/^(\s*[-*•])\s+/, (m, bullet) =>
          bullet === "•" ? "- " : `${bullet} `
        )
      )
      .join("\n");

    // 7) Build a compact Markdown "Sources" footer (if we captured any)
    let sourcesFooter = "";
    if (Object.keys(sourcesMap).length) {
      const ids = Object.keys(sourcesMap).sort((a, b) => Number(a) - Number(b));
      const links = ids.map((n) => `[${n}](${sourcesMap[n]})`).join(" ");
      sourcesFooter = `\n\n**Sources:** ${links}`;
    }

    // 8) Restore inline & fenced code blocks
    t = restoreAll(t, inlines);
    t = restoreAll(t, fences);

    // 9) Return trimmed Markdown plus optional footer
    return (t.trim() + sourcesFooter).trim();
  }

  static formatStructuredData(data: any): string {
    if (typeof data === "string") return this.formatMessage(data);
    if (data?.type === "employee_list") return this.formatEmployeeList(data.employees || []);
    if (data?.type === "project_teams") return this.formatProjectTeams(data.teams || []);
    if (data?.type === "document_analysis") return this.formatDocumentAnalysis(data.analysis || {});
    // Fallback: pretty-print JSON inside a fenced block
    return "```json\n" + JSON.stringify(data, null, 2) + "\n```";
  }

  // === Structured: Employees -> Markdown ===
  private static formatEmployeeList(employees: any[]): string {
    const parts: string[] = [];
    parts.push("### 👥 Employee Directory\n");
    employees.forEach((emp) => {
      const name = emp?.name ?? "Unknown";
      const id = emp?.id ?? "—";
      parts.push(`**${name}** _(ID: ${id})_`);
      const rows: string[] = [];
      if (emp?.department) rows.push(`- **Department:** ${emp.department}`);
      if (emp?.position) rows.push(`- **Position:** ${emp.position}`);
      if (emp?.status) rows.push(`- **Status:** ${emp.status}`);
      if (emp?.email) rows.push(`- **Email:** <mailto:${emp.email}>`);
      if (rows.length) parts.push(rows.join("\n"));
      parts.push(""); // blank line between cards
    });
    parts.push(`_Found ${employees.length} employee${employees.length === 1 ? "" : "s"}._`);
    return parts.join("\n");
  }

  // === Structured: Project Teams -> Markdown ===
  private static formatProjectTeams(teams: any[]): string {
    const parts: string[] = [];
    parts.push("### 🎯 Project Teams\n");
    teams.forEach((team) => {
      const name = team?.name ?? "Unnamed Team";
      const status = team?.status ? ` — **${team.status}**` : "";
      parts.push(`**${name}**${status}`);
      if (team?.project) parts.push(`- **Project:** ${team.project}`);
      if (team?.lead) parts.push(`- **Lead:** ${team.lead}`);
      if (Array.isArray(team?.members) && team.members.length) {
        parts.push(`- **Members:** ${team.members.map((m: string) => `\`${m}\``).join(", ")}`);
      }
      parts.push(""); // blank line between teams
    });
    return parts.join("\n");
  }

  // === Structured: Document Analysis -> Markdown ===
  private static formatDocumentAnalysis(analysis: any): string {
    const parts: string[] = [];
    parts.push("### 📊 Document Analysis\n");

    if (analysis?.summary) {
      parts.push("#### Summary");
      parts.push(this.formatMessage(String(analysis.summary)));
      parts.push("");
    }

    if (Array.isArray(analysis?.key_points) && analysis.key_points.length) {
      parts.push("#### Key Points");
      for (const p of analysis.key_points) {
        // Each key point may itself contain markdown—format it
        parts.push(`- ${this.formatMessage(String(p))}`);
      }
      parts.push("");
    }

    if (Array.isArray(analysis?.documents) && analysis.documents.length) {
      parts.push("#### Analyzed Documents");
      for (const d of analysis.documents) {
        const name = d?.name ?? "Untitled";
        const type = d?.type ? ` (${d.type})` : "";
        const pages = d?.pages ? ` — ${d.pages} pages` : "";
        parts.push(`- **${name}**${type}${pages}`);
      }
      parts.push("");
    }

    return parts.join("\n").trim();
  }
}
